// Quiz data for EssayRight - Educational quizzes for essay writing skills
// Systematic naming convention for future Supabase backend migration

export const quizSets = [
  // Letters/emails to inform - Sports Day Information
  {
    id: 'quiz-inform-sports-day',
    essayId: 'inform-sports-day',
    title: 'Sports Day Information - Writing Skills Quiz',
    category: 'Letters/emails to inform',
    description: 'Test your understanding of informative letter writing using the Sports Day example',
    difficulty: 'intermediate',
    totalQuestions: 10,
    timeLimit: 300, // 5 minutes
    image: '🏃‍♂️',
    questions: [
      {
        id: 1,
        question: 'What is the primary purpose of an informative letter?',
        type: 'multiple-choice',
        options: [
          'To persuade the reader to take action',
          'To provide factual information about an event or situation',
          'To express personal opinions',
          'To request something from the reader'
        ],
        correct: 'To provide factual information about an event or situation',
        explanation: 'Informative letters are designed to share facts and details about specific events, situations, or procedures.'
      },
      {
        id: 2,
        question: 'In the Sports Day letter, what essential information is provided about student dress code?',
        type: 'multiple-choice',
        options: [
          'Students should wear formal uniform',
          'Students need to wear PE uniform with no changing facilities available',
          'Students can wear casual clothes',
          'Dress code is not mentioned'
        ],
        correct: 'Students need to wear PE uniform with no changing facilities available',
        explanation: 'The letter clearly states students need PE uniform because there are no changing facilities at the sports ground.'
      },
      {
        id: 3,
        question: 'What phrase is used to introduce the main purpose of the Sports Day letter?',
        type: 'fill-in-blank',
        question_text: 'Complete the phrase: "I would like to _____ you of arrangements for that day."',
        correct: 'inform',
        explanation: 'The phrase "I would like to inform you of" is a standard opening for informative letters.'
      },
      {
        id: 4,
        question: 'Which of these is NOT mentioned as something students should bring on Sports Day?',
        type: 'multiple-choice',
        options: [
          'Packed lunch',
          'Water bottles',
          'PE uniform',
          'Sports equipment'
        ],
        correct: 'Water bottles',
        explanation: 'The letter specifically states students do not need to bring water as the school will provide free bottles.'
      },
      {
        id: 5,
        question: 'What is the appropriate closing for a letter to parents from a school department?',
        type: 'multiple-choice',
        options: [
          'Yours faithfully',
          'Yours sincerely',
          'Best regards',
          'Kind regards'
        ],
        correct: 'Yours sincerely',
        explanation: 'When writing to a known group of people (like parents), "Yours sincerely" is more friendly and appropriate.'
      },
      {
        id: 6,
        question: 'The Sports Day timeline shows events from 9:30 am to midday, then from _____ to _____.',
        type: 'fill-in-blank',
        question_text: 'Complete the timeline: Events resume at _____ and prize-giving is at _____.',
        correct: '1:00 pm, 2:30 pm',
        explanation: 'The letter states events re-commence at 1:00 pm and prize-giving takes place at 2:30 pm.'
      },
      {
        id: 7,
        question: 'What contact method is provided for questions about Sports Day?',
        type: 'multiple-choice',
        options: [
          'Phone number',
          'Email address',
          'Office visit',
          'Written letter'
        ],
        correct: 'Email address',
        explanation: 'The letter provides the email address sportsteacher@hkschool.com for questions.'
      },
      {
        id: 8,
        question: 'True or False: Students will be brought back to school for dismissal after Sports Day.',
        type: 'true-false',
        correct: false,
        explanation: 'The letter clearly states dismissal will take place at the sports ground, not back at school.'
      },
      {
        id: 9,
        question: 'What makes this letter tone appropriate for the audience?',
        type: 'multiple-choice',
        options: [
          'It uses very formal academic language',
          'It provides clear, factual information in a friendly tone',
          'It uses casual, informal language',
          'It includes personal opinions'
        ],
        correct: 'It provides clear, factual information in a friendly tone',
        explanation: 'The tone is professional yet friendly, appropriate for communicating with parents about school events.'
      },
      {
        id: 10,
        question: 'Which phrase would be appropriate to start an informative letter?',
        type: 'multiple-choice',
        options: [
          'I am writing to complain about...',
          'I would like to inform you of...',
          'I am requesting that you...',
          'I strongly believe that...'
        ],
        correct: 'I would like to inform you of...',
        explanation: 'This is a standard opening phrase for informative letters, clearly stating the purpose.'
      }
    ]
  },

  // Letters/emails of invitation - Prize-Giving Ceremony
  {
    id: 'quiz-invitation-prize-giving',
    essayId: 'invitation-prize-giving',
    title: 'Prize-Giving Ceremony Invitation - Writing Skills Quiz',
    category: 'Letters/emails of invitation',
    description: 'Learn invitation letter writing skills through the Prize-Giving Ceremony example',
    difficulty: 'intermediate',
    totalQuestions: 10,
    timeLimit: 300,
    image: '🏆',
    questions: [
      {
        id: 1,
        question: 'What is the main purpose of an invitation letter?',
        type: 'multiple-choice',
        options: [
          'To provide information only',
          'To invite someone to attend an event and make them feel welcome',
          'To complain about something',
          'To request a favor'
        ],
        correct: 'To invite someone to attend an event and make them feel welcome',
        explanation: 'Invitation letters should make the recipient feel honored and welcome to attend.'
      },
      {
        id: 2,
        question: 'In formal invitations, what essential details must be included?',
        type: 'multiple-choice',
        options: [
          'Only the date and time',
          'Date, time, venue, and dress code',
          'Just the event name',
          'Only contact information'
        ],
        correct: 'Date, time, venue, and dress code',
        explanation: 'All practical details are essential for guests to attend appropriately.'
      },
      {
        id: 3,
        question: 'What phrase expresses honor in extending an invitation?',
        type: 'fill-in-blank',
        question_text: 'Complete: "I would be _____ if you could attend..."',
        correct: 'honored',
        explanation: 'This phrase shows respect and makes the invitation feel special.'
      },
      {
        id: 4,
        question: 'True or False: Invitation letters should include an RSVP request.',
        type: 'true-false',
        correct: true,
        explanation: 'RSVP helps organizers plan for the correct number of attendees.'
      },
      {
        id: 5,
        question: 'What tone should an invitation letter maintain?',
        type: 'multiple-choice',
        options: [
          'Demanding and urgent',
          'Warm, welcoming, and respectful',
          'Casual and informal only',
          'Cold and businesslike'
        ],
        correct: 'Warm, welcoming, and respectful',
        explanation: 'Invitations should make recipients feel valued and welcome.'
      },
      {
        id: 6,
        question: 'Which closing is most appropriate for a formal invitation?',
        type: 'multiple-choice',
        options: [
          'Yours faithfully',
          'Yours sincerely',
          'Cheers',
          'Best'
        ],
        correct: 'Yours sincerely',
        explanation: 'For formal invitations to known recipients, "Yours sincerely" is appropriate.'
      },
      {
        id: 7,
        question: 'What should be mentioned about the significance of the event?',
        type: 'multiple-choice',
        options: [
          'Nothing about its importance',
          'Why the event is meaningful and why their presence matters',
          'Only practical details',
          'Personal opinions only'
        ],
        correct: 'Why the event is meaningful and why their presence matters',
        explanation: 'Explaining significance helps the recipient understand the importance of attending.'
      },
      {
        id: 8,
        question: 'In the Prize-Giving example, what makes Mr. Chung suitable as guest of honor?',
        type: 'fill-in-blank',
        question_text: 'Mr. Chung is suitable because of his _____ in the fashion industry.',
        correct: 'expertise',
        explanation: 'Highlighting the guest\'s relevant qualifications shows why they were chosen.'
      },
      {
        id: 9,
        question: 'What should an invitation letter avoid?',
        type: 'multiple-choice',
        options: [
          'Specific details about the event',
          'Being too pushy or demanding attendance',
          'Contact information',
          'Warm, welcoming language'
        ],
        correct: 'Being too pushy or demanding attendance',
        explanation: 'Invitations should be gracious and allow the recipient to decline politely.'
      },
      {
        id: 10,
        question: 'True or False: It\'s appropriate to mention refreshments in an invitation.',
        type: 'true-false',
        correct: true,
        explanation: 'Mentioning refreshments helps guests know what to expect and shows hospitality.'
      }
    ]
  },

  // Letters/emails of application - Trainee Reporter
  {
    id: 'quiz-application-trainee-reporter',
    essayId: 'application-trainee-reporter',
    title: 'Job Application - Writing Skills Quiz',
    category: 'Letters/emails of application',
    description: 'Master job application letter writing through the trainee reporter example',
    difficulty: 'advanced',
    totalQuestions: 12,
    timeLimit: 360,
    image: '📰',
    questions: [
      {
        id: 1,
        question: 'What is the primary purpose of a job application letter?',
        type: 'multiple-choice',
        options: [
          'To provide personal information only',
          'To convince the employer you are the best candidate for the position',
          'To ask about salary details',
          'To criticize other candidates'
        ],
        correct: 'To convince the employer you are the best candidate for the position',
        explanation: 'Application letters should persuasively present your qualifications and suitability.'
      },
      {
        id: 2,
        question: 'How should you reference the job advertisement in your application?',
        type: 'multiple-choice',
        options: [
          'Don\'t mention it',
          'Mention where and when you saw the advertisement',
          'Only mention the job title',
          'Criticize the advertisement'
        ],
        correct: 'Mention where and when you saw the advertisement',
        explanation: 'This shows you\'re responding to a specific opportunity and helps the employer track applications.'
      },
      {
        id: 3,
        question: 'Complete this application opening: "I am writing to _____ for the position of..."',
        type: 'fill-in-blank',
        question_text: 'Fill in the blank: "I am writing to _____ for the position of trainee reporter."',
        correct: 'apply',
        explanation: 'This is the standard opening phrase for job applications.'
      },
      {
        id: 4,
        question: 'What should you emphasize when discussing your qualifications?',
        type: 'multiple-choice',
        options: [
          'Only your academic achievements',
          'Relevant skills, experience, and achievements that match the job requirements',
          'Personal hobbies unrelated to the job',
          'Why you need the job'
        ],
        correct: 'Relevant skills, experience, and achievements that match the job requirements',
        explanation: 'Focus on qualifications that directly relate to the position you\'re applying for.'
      },
      {
        id: 5,
        question: 'True or False: You should mention your availability for an interview.',
        type: 'true-false',
        correct: true,
        explanation: 'Showing availability demonstrates enthusiasm and helps with scheduling.'
      },
      {
        id: 6,
        question: 'Which phrase best expresses enthusiasm for the position?',
        type: 'multiple-choice',
        options: [
          'I suppose I could do this job',
          'I am very interested in this position and eager to contribute',
          'I need any job available',
          'This job seems okay'
        ],
        correct: 'I am very interested in this position and eager to contribute',
        explanation: 'Enthusiasm and eagerness to contribute show genuine interest in the role.'
      },
      {
        id: 7,
        question: 'What should you include about your experience?',
        type: 'multiple-choice',
        options: [
          'Every job you\'ve ever had',
          'Only paid work experience',
          'Relevant experience that demonstrates applicable skills',
          'No experience details'
        ],
        correct: 'Relevant experience that demonstrates applicable skills',
        explanation: 'Focus on experiences that show you have skills relevant to the job.'
      },
      {
        id: 8,
        question: 'How should you conclude an application letter?',
        type: 'fill-in-blank',
        question_text: 'Complete: "I look forward to _____ from you soon."',
        correct: 'hearing',
        explanation: 'This standard closing shows anticipation for a response.'
      },
      {
        id: 9,
        question: 'What tone should a job application maintain?',
        type: 'multiple-choice',
        options: [
          'Overly casual and friendly',
          'Professional, confident, and enthusiastic',
          'Desperate and pleading',
          'Arrogant and demanding'
        ],
        correct: 'Professional, confident, and enthusiastic',
        explanation: 'This tone shows you\'re serious about the position while remaining personable.'
      },
      {
        id: 10,
        question: 'True or False: You should mention specific achievements with concrete examples.',
        type: 'true-false',
        correct: true,
        explanation: 'Specific examples make your claims more credible and memorable.'
      },
      {
        id: 11,
        question: 'Which is the most appropriate closing for a job application?',
        type: 'multiple-choice',
        options: [
          'Yours faithfully',
          'Yours sincerely',
          'Best wishes',
          'Cheers'
        ],
        correct: 'Yours faithfully',
        explanation: 'Use "Yours faithfully" when you don\'t know the recipient\'s name (Dear Sir/Madam).'
      },
      {
        id: 12,
        question: 'What should you avoid in a job application letter?',
        type: 'multiple-choice',
        options: [
          'Mentioning relevant qualifications',
          'Showing enthusiasm',
          'Being too lengthy or including irrelevant information',
          'Professional language'
        ],
        correct: 'Being too lengthy or including irrelevant information',
        explanation: 'Keep applications concise and focused on relevant qualifications only.'
      }
    ]
  },

  // Responding to letters/emails of complaint - Bookshop Service
  {
    id: 'quiz-complaint-response-bookshop',
    essayId: 'complaint-response-bookshop',
    title: 'Complaint Response - Writing Skills Quiz',
    category: 'Responding to letters/emails of complaint',
    description: 'Learn how to respond professionally to complaints using the bookshop example',
    difficulty: 'advanced',
    totalQuestions: 10,
    timeLimit: 300,
    image: '📚',
    questions: [
      {
        id: 1,
        question: 'What should be the first step when responding to a complaint?',
        type: 'multiple-choice',
        options: [
          'Defend your company immediately',
          'Acknowledge the complaint and apologize for the inconvenience',
          'Ignore the complaint',
          'Blame the customer'
        ],
        correct: 'Acknowledge the complaint and apologize for the inconvenience',
        explanation: 'Acknowledgment and apology show professionalism and help defuse tension.'
      },
      {
        id: 2,
        question: 'What tone should a complaint response maintain?',
        type: 'multiple-choice',
        options: [
          'Defensive and argumentative',
          'Professional, empathetic, and solution-focused',
          'Casual and dismissive',
          'Overly emotional'
        ],
        correct: 'Professional, empathetic, and solution-focused',
        explanation: 'This tone helps resolve the issue while maintaining customer relationships.'
      },
      {
        id: 3,
        question: 'Complete this apology phrase: "I sincerely _____ for the inconvenience caused."',
        type: 'fill-in-blank',
        question_text: 'Fill in: "I sincerely _____ for the inconvenience caused."',
        correct: 'apologize',
        explanation: 'A sincere apology is essential in complaint responses.'
      },
      {
        id: 4,
        question: 'What should you do after acknowledging the complaint?',
        type: 'multiple-choice',
        options: [
          'End the letter immediately',
          'Explain what went wrong and offer a solution',
          'Make excuses',
          'Blame other departments'
        ],
        correct: 'Explain what went wrong and offer a solution',
        explanation: 'Customers want to understand the problem and see concrete solutions.'
      },
      {
        id: 5,
        question: 'True or False: You should always offer compensation in complaint responses.',
        type: 'true-false',
        correct: false,
        explanation: 'Compensation depends on the situation; sometimes process improvements or explanations are sufficient.'
      },
      {
        id: 6,
        question: 'What phrase shows you take responsibility?',
        type: 'multiple-choice',
        options: [
          'It\'s not our fault',
          'We take full responsibility for this oversight',
          'These things happen',
          'You should have known better'
        ],
        correct: 'We take full responsibility for this oversight',
        explanation: 'Taking responsibility shows accountability and professionalism.'
      },
      {
        id: 7,
        question: 'How should you conclude a complaint response?',
        type: 'multiple-choice',
        options: [
          'With a threat to the customer',
          'By dismissing their concerns',
          'With reassurance and an invitation for future contact',
          'By ignoring the issue'
        ],
        correct: 'With reassurance and an invitation for future contact',
        explanation: 'This maintains the relationship and shows ongoing commitment to service.'
      },
      {
        id: 8,
        question: 'What should you avoid when responding to complaints?',
        type: 'multiple-choice',
        options: [
          'Apologizing sincerely',
          'Making excuses or shifting blame',
          'Offering solutions',
          'Professional language'
        ],
        correct: 'Making excuses or shifting blame',
        explanation: 'Excuses and blame-shifting damage customer relationships and appear unprofessional.'
      },
      {
        id: 9,
        question: 'Fill in: "We have taken immediate _____ to prevent this from happening again."',
        type: 'fill-in-blank',
        question_text: 'Complete: "We have taken immediate _____ to prevent this from happening again."',
        correct: 'action',
        explanation: 'This shows proactive measures to address the underlying problem.'
      },
      {
        id: 10,
        question: 'True or False: A good complaint response can turn an unhappy customer into a loyal one.',
        type: 'true-false',
        correct: true,
        explanation: 'Excellent complaint handling often creates stronger customer relationships than if no problem had occurred.'
      }
    ]
  },

  // Informal letters/emails - Weekend Trip
  {
    id: 'quiz-informal-weekend-trip',
    essayId: 'informal-weekend-trip',
    title: 'Informal Email - Writing Skills Quiz',
    category: 'Informal letters/emails',
    description: 'Master informal email writing through the weekend trip example',
    difficulty: 'beginner',
    totalQuestions: 8,
    timeLimit: 240,
    image: '🏖️',
    questions: [
      {
        id: 1,
        question: 'What makes an email "informal"?',
        type: 'multiple-choice',
        options: [
          'It has no structure',
          'It uses casual language and personal tone with friends or family',
          'It contains no useful information',
          'It\'s always very short'
        ],
        correct: 'It uses casual language and personal tone with friends or family',
        explanation: 'Informal emails are characterized by relaxed language and personal connection.'
      },
      {
        id: 2,
        question: 'How should you start an informal email to a friend?',
        type: 'multiple-choice',
        options: [
          'Dear Sir/Madam',
          'Hi [Name] or Hey [Name]',
          'To Whom It May Concern',
          'Yours faithfully'
        ],
        correct: 'Hi [Name] or Hey [Name]',
        explanation: 'Informal greetings are casual and use the person\'s first name.'
      },
      {
        id: 3,
        question: 'True or False: Informal emails can include contractions like "I\'m" and "we\'ll".',
        type: 'true-false',
        correct: true,
        explanation: 'Contractions are perfectly acceptable and natural in informal writing.'
      },
      {
        id: 4,
        question: 'What should you include when describing plans to friends?',
        type: 'multiple-choice',
        options: [
          'Only basic facts',
          'Exciting details, personal feelings, and what makes it special',
          'Formal language only',
          'No emotional content'
        ],
        correct: 'Exciting details, personal feelings, and what makes it special',
        explanation: 'Informal emails should be engaging and share your enthusiasm.'
      },
      {
        id: 5,
        question: 'Complete this informal closing: "Looking forward to _____ from you soon!"',
        type: 'fill-in-blank',
        question_text: 'Fill in: "Looking forward to _____ from you soon!"',
        correct: 'hearing',
        explanation: 'This is a friendly, informal way to end an email to a friend.'
      },
      {
        id: 6,
        question: 'Which closing is appropriate for an informal email?',
        type: 'multiple-choice',
        options: [
          'Yours faithfully',
          'Best wishes or Love',
          'Yours sincerely',
          'Respectfully yours'
        ],
        correct: 'Best wishes or Love',
        explanation: 'These closings match the casual, friendly tone of informal emails.'
      },
      {
        id: 7,
        question: 'What makes the weekend trip email engaging?',
        type: 'multiple-choice',
        options: [
          'It uses very formal language',
          'It shares excitement and specific details about activities',
          'It\'s extremely brief',
          'It focuses only on logistics'
        ],
        correct: 'It shares excitement and specific details about activities',
        explanation: 'Personal enthusiasm and vivid details make informal emails interesting to read.'
      },
      {
        id: 8,
        question: 'True or False: Informal emails should still have a clear structure (greeting, main content, closing).',
        type: 'true-false',
        correct: true,
        explanation: 'Even informal emails benefit from basic structure to be clear and easy to follow.'
      }
    ]
  },

  // Letters/emails of request - Laboratory Visit
  {
    id: 'quiz-request-lab-visit',
    essayId: 'request-lab-visit',
    title: 'Laboratory Visit Request - Writing Skills Quiz',
    category: 'Letters/emails of request',
    description: 'Learn how to write effective request letters using the laboratory visit example',
    difficulty: 'intermediate',
    totalQuestions: 10,
    timeLimit: 300,
    image: '🔬',
    questions: [
      {
        id: 1,
        question: 'What is the primary purpose of a request letter?',
        type: 'multiple-choice',
        options: [
          'To provide information only',
          'To politely ask for something specific',
          'To complain about a service',
          'To give instructions'
        ],
        correct: 'To politely ask for something specific',
        explanation: 'Request letters are designed to politely ask for favors, permissions, or services.'
      },
      {
        id: 2,
        question: 'What should you include when making a request?',
        type: 'multiple-choice',
        options: [
          'Only what you want',
          'Clear explanation of what you need and why',
          'Demands without explanation',
          'Personal complaints'
        ],
        correct: 'Clear explanation of what you need and why',
        explanation: 'Effective requests explain both what you need and the reason behind the request.'
      },
      {
        id: 3,
        question: 'Complete this polite request phrase: "I would be _____ if you could..."',
        type: 'fill-in-blank',
        question_text: 'Fill in: "I would be _____ if you could arrange this visit."',
        correct: 'grateful',
        explanation: 'Expressing gratitude in advance shows politeness and appreciation.'
      },
      {
        id: 4,
        question: 'True or False: Request letters should include specific dates and times when possible.',
        type: 'true-false',
        correct: true,
        explanation: 'Specific details help the recipient understand exactly what you need and when.'
      },
      {
        id: 5,
        question: 'What tone should a request letter maintain?',
        type: 'multiple-choice',
        options: [
          'Demanding and urgent',
          'Polite, respectful, and appreciative',
          'Casual and informal',
          'Aggressive and persistent'
        ],
        correct: 'Polite, respectful, and appreciative',
        explanation: 'A respectful tone increases the likelihood of a positive response.'
      },
      {
        id: 6,
        question: 'How should you conclude a request letter?',
        type: 'multiple-choice',
        options: [
          'With a demand for immediate action',
          'With thanks in advance and contact information',
          'With a threat if not fulfilled',
          'With no closing statement'
        ],
        correct: 'With thanks in advance and contact information',
        explanation: 'Thanking in advance shows courtesy and providing contact info facilitates response.'
      },
      {
        id: 7,
        question: 'What should you avoid in a request letter?',
        type: 'multiple-choice',
        options: [
          'Being specific about your needs',
          'Being overly demanding or entitled',
          'Providing contact information',
          'Expressing gratitude'
        ],
        correct: 'Being overly demanding or entitled',
        explanation: 'Demanding tone can create negative impressions and reduce cooperation.'
      },
      {
        id: 8,
        question: 'Fill in: "I would appreciate it if you could _____ this matter."',
        type: 'fill-in-blank',
        question_text: 'Complete: "I would appreciate it if you could _____ this matter."',
        correct: 'consider',
        explanation: 'This phrase politely asks for consideration without being demanding.'
      },
      {
        id: 9,
        question: 'True or False: You should explain the benefits or importance of your request.',
        type: 'true-false',
        correct: true,
        explanation: 'Explaining benefits helps the recipient understand why they should grant your request.'
      },
      {
        id: 10,
        question: 'Which closing is most appropriate for a request letter?',
        type: 'multiple-choice',
        options: [
          'Yours faithfully',
          'Yours sincerely',
          'Best regards',
          'Cheers'
        ],
        correct: 'Yours sincerely',
        explanation: 'For formal requests to known recipients, "Yours sincerely" is appropriate.'
      }
    ]
  },

  // Letters/emails of advice - Friendship Advice
  {
    id: 'quiz-advice-friendship',
    essayId: 'advice-friendship',
    title: 'Friendship Advice - Writing Skills Quiz',
    category: 'Letters/emails of advice',
    description: 'Master advice letter writing through the friendship advice example',
    difficulty: 'intermediate',
    totalQuestions: 10,
    timeLimit: 300,
    image: '💡',
    questions: [
      {
        id: 1,
        question: 'What is the main purpose of an advice letter?',
        type: 'multiple-choice',
        options: [
          'To criticize someone\'s decisions',
          'To provide helpful guidance and suggestions',
          'To impose your opinions',
          'To avoid the person\'s problems'
        ],
        correct: 'To provide helpful guidance and suggestions',
        explanation: 'Advice letters should offer constructive guidance to help someone solve problems.'
      },
      {
        id: 2,
        question: 'How should you start an advice letter?',
        type: 'multiple-choice',
        options: [
          'By immediately giving solutions',
          'By acknowledging the person\'s situation and showing empathy',
          'By criticizing their choices',
          'By avoiding the main issue'
        ],
        correct: 'By acknowledging the person\'s situation and showing empathy',
        explanation: 'Showing understanding creates a supportive foundation for giving advice.'
      },
      {
        id: 3,
        question: 'Complete this empathetic phrase: "I understand that you must be feeling..."',
        type: 'fill-in-blank',
        question_text: 'Fill in: "I understand that you must be feeling _____ about this situation."',
        correct: 'concerned',
        explanation: 'Acknowledging emotions shows empathy and understanding.'
      },
      {
        id: 4,
        question: 'True or False: Advice should be presented as suggestions rather than commands.',
        type: 'true-false',
        correct: true,
        explanation: 'Suggestions allow the person to make their own decisions while offering guidance.'
      },
      {
        id: 5,
        question: 'What phrase is appropriate for giving advice?',
        type: 'multiple-choice',
        options: [
          'You must do this immediately',
          'I suggest you might consider...',
          'You have no choice but to...',
          'You should have known better'
        ],
        correct: 'I suggest you might consider...',
        explanation: 'This phrase offers advice respectfully without being demanding.'
      },
      {
        id: 6,
        question: 'How should you present multiple suggestions?',
        type: 'multiple-choice',
        options: [
          'As the only possible solutions',
          'As various options to consider',
          'As mandatory requirements',
          'As criticism of past actions'
        ],
        correct: 'As various options to consider',
        explanation: 'Presenting options allows the person to choose what works best for them.'
      },
      {
        id: 7,
        question: 'What should you avoid when giving advice?',
        type: 'multiple-choice',
        options: [
          'Being supportive',
          'Being judgmental or critical',
          'Offering practical suggestions',
          'Showing empathy'
        ],
        correct: 'Being judgmental or critical',
        explanation: 'Judgment and criticism can damage relationships and make advice less effective.'
      },
      {
        id: 8,
        question: 'Fill in: "Have you considered _____ to your friend about this?"',
        type: 'fill-in-blank',
        question_text: 'Complete: "Have you considered _____ to your friend about this?"',
        correct: 'talking',
        explanation: 'This suggests communication as a solution in a gentle, questioning way.'
      },
      {
        id: 9,
        question: 'True or False: You should offer to help or support the person further.',
        type: 'true-false',
        correct: true,
        explanation: 'Offering continued support shows genuine care and maintains the relationship.'
      },
      {
        id: 10,
        question: 'How should you conclude an advice letter?',
        type: 'multiple-choice',
        options: [
          'With a final demand',
          'With encouragement and availability for further help',
          'With criticism of their situation',
          'With no closing remarks'
        ],
        correct: 'With encouragement and availability for further help',
        explanation: 'Positive closure with continued support strengthens the relationship.'
      }
    ]
  },

  // Letters/emails of complaint - Bookshop Service
  {
    id: 'quiz-complaint-bookshop-service',
    essayId: 'complaint-bookshop-service',
    title: 'Service Complaint - Writing Skills Quiz',
    category: 'Letters/emails of complaint',
    description: 'Learn how to write effective complaint letters using the bookshop service example',
    difficulty: 'intermediate',
    totalQuestions: 10,
    timeLimit: 300,
    image: '⚠️',
    questions: [
      {
        id: 1,
        question: 'What is the primary purpose of a complaint letter?',
        type: 'multiple-choice',
        options: [
          'To express anger and frustration',
          'To seek resolution for a problem or poor service',
          'To threaten the business',
          'To get free products'
        ],
        correct: 'To seek resolution for a problem or poor service',
        explanation: 'Complaint letters should focus on getting problems resolved constructively.'
      },
      {
        id: 2,
        question: 'How should you begin a complaint letter?',
        type: 'multiple-choice',
        options: [
          'With angry accusations',
          'By clearly stating the problem and when it occurred',
          'With threats of legal action',
          'By demanding immediate compensation'
        ],
        correct: 'By clearly stating the problem and when it occurred',
        explanation: 'Clear, factual description of the problem helps the recipient understand the issue.'
      },
      {
        id: 3,
        question: 'Complete this complaint opening: "I am writing to _____ about..."',
        type: 'fill-in-blank',
        question_text: 'Fill in: "I am writing to _____ about the poor service I received."',
        correct: 'complain',
        explanation: 'This is a direct, professional way to state the purpose of your letter.'
      },
      {
        id: 4,
        question: 'True or False: You should include specific details about what went wrong.',
        type: 'true-false',
        correct: true,
        explanation: 'Specific details help the recipient understand exactly what happened and how to fix it.'
      },
      {
        id: 5,
        question: 'What tone should a complaint letter maintain?',
        type: 'multiple-choice',
        options: [
          'Angry and aggressive',
          'Firm but professional and respectful',
          'Sarcastic and insulting',
          'Overly emotional'
        ],
        correct: 'Firm but professional and respectful',
        explanation: 'A professional tone is more likely to get positive results than aggression.'
      },
      {
        id: 6,
        question: 'What should you request in a complaint letter?',
        type: 'multiple-choice',
        options: [
          'Unreasonable compensation',
          'Specific, reasonable action to resolve the problem',
          'Punishment for staff members',
          'Public apology'
        ],
        correct: 'Specific, reasonable action to resolve the problem',
        explanation: 'Clear, reasonable requests are more likely to be granted.'
      },
      {
        id: 7,
        question: 'How should you support your complaint?',
        type: 'multiple-choice',
        options: [
          'With emotional appeals only',
          'With facts, dates, and evidence',
          'With threats and ultimatums',
          'With personal attacks'
        ],
        correct: 'With facts, dates, and evidence',
        explanation: 'Factual evidence makes your complaint more credible and actionable.'
      },
      {
        id: 8,
        question: 'Fill in: "I expect you to _____ this matter promptly."',
        type: 'fill-in-blank',
        question_text: 'Complete: "I expect you to _____ this matter promptly."',
        correct: 'resolve',
        explanation: 'This phrase clearly states your expectation for action.'
      },
      {
        id: 9,
        question: 'True or False: You should set a reasonable deadline for response.',
        type: 'true-false',
        correct: true,
        explanation: 'A reasonable deadline encourages prompt action while being fair to the recipient.'
      },
      {
        id: 10,
        question: 'How should you conclude a complaint letter?',
        type: 'multiple-choice',
        options: [
          'With threats of legal action',
          'With expectation of prompt resolution and contact information',
          'With insults about the company',
          'With no closing statement'
        ],
        correct: 'With expectation of prompt resolution and contact information',
        explanation: 'Professional closure maintains effectiveness while providing means for response.'
      }
    ]
  },

  // Letters/emails to the editor - Teen Response
  {
    id: 'quiz-editor-response-teens',
    essayId: 'editor-response-teens',
    title: 'Letter to Editor - Writing Skills Quiz',
    category: 'Letters/emails to the editor',
    description: 'Master opinion letter writing through the teen response example',
    difficulty: 'advanced',
    totalQuestions: 10,
    timeLimit: 300,
    image: '📰',
    questions: [
      {
        id: 1,
        question: 'What is the main purpose of a letter to the editor?',
        type: 'multiple-choice',
        options: [
          'To get personal attention',
          'To express public opinion on important issues',
          'To advertise products',
          'To complain about newspaper content'
        ],
        correct: 'To express public opinion on important issues',
        explanation: 'Letters to editors allow public discourse on community and societal issues.'
      },
      {
        id: 2,
        question: 'How should you reference the original article?',
        type: 'multiple-choice',
        options: [
          'Don\'t mention it at all',
          'Mention the title, date, and specific points you\'re addressing',
          'Only mention the author\'s name',
          'Criticize it without specifics'
        ],
        correct: 'Mention the title, date, and specific points you\'re addressing',
        explanation: 'Clear reference helps readers understand the context of your response.'
      },
      {
        id: 3,
        question: 'Complete this reference phrase: "I am writing in response to..."',
        type: 'fill-in-blank',
        question_text: 'Fill in: "I am writing in response to _____ article about teenagers."',
        correct: 'your',
        explanation: 'This clearly identifies which article you\'re responding to.'
      },
      {
        id: 4,
        question: 'True or False: You should present evidence to support your opinion.',
        type: 'true-false',
        correct: true,
        explanation: 'Evidence and examples make your arguments more convincing and credible.'
      },
      {
        id: 5,
        question: 'What makes an effective argument in a letter to the editor?',
        type: 'multiple-choice',
        options: [
          'Emotional appeals only',
          'Logical reasoning supported by facts and examples',
          'Personal attacks on the author',
          'Unsupported opinions'
        ],
        correct: 'Logical reasoning supported by facts and examples',
        explanation: 'Well-reasoned arguments with evidence are more persuasive to readers.'
      },
      {
        id: 6,
        question: 'How should you address opposing viewpoints?',
        type: 'multiple-choice',
        options: [
          'Ignore them completely',
          'Attack them personally',
          'Acknowledge and respectfully counter them',
          'Dismiss them without explanation'
        ],
        correct: 'Acknowledge and respectfully counter them',
        explanation: 'Respectful acknowledgment shows fairness and strengthens your position.'
      },
      {
        id: 7,
        question: 'What tone should a letter to the editor maintain?',
        type: 'multiple-choice',
        options: [
          'Angry and confrontational',
          'Respectful but assertive',
          'Overly casual',
          'Apologetic and uncertain'
        ],
        correct: 'Respectful but assertive',
        explanation: 'This tone commands respect while maintaining civility in public discourse.'
      },
      {
        id: 8,
        question: 'Fill in: "I believe that teenagers are _____ than the article suggests."',
        type: 'fill-in-blank',
        question_text: 'Complete: "I believe that teenagers are _____ than the article suggests."',
        correct: 'better',
        explanation: 'This shows you\'re presenting a counter-argument to the original article.'
      },
      {
        id: 9,
        question: 'True or False: Letters to the editor should be concise and focused.',
        type: 'true-false',
        correct: true,
        explanation: 'Newspapers have limited space, so concise, focused letters are more likely to be published.'
      },
      {
        id: 10,
        question: 'How should you conclude a letter to the editor?',
        type: 'multiple-choice',
        options: [
          'With a personal attack',
          'With a call to action or summary of your main point',
          'With an apology',
          'With no conclusion'
        ],
        correct: 'With a call to action or summary of your main point',
        explanation: 'Strong conclusions reinforce your message and may inspire reader action.'
      }
    ]
  },

  // Letters/emails of enquiry - Table Tennis
  {
    id: 'quiz-enquiry-table-tennis',
    essayId: 'enquiry-table-tennis',
    title: 'Product Enquiry - Writing Skills Quiz',
    category: 'Letters/emails of enquiry',
    description: 'Learn enquiry letter writing through the table tennis equipment example',
    difficulty: 'intermediate',
    totalQuestions: 10,
    timeLimit: 300,
    image: '🏓',
    questions: [
      {
        id: 1,
        question: 'What is the main purpose of an enquiry letter?',
        type: 'multiple-choice',
        options: [
          'To make a complaint',
          'To request specific information about products or services',
          'To place an order',
          'To cancel a service'
        ],
        correct: 'To request specific information about products or services',
        explanation: 'Enquiry letters seek information to help make informed decisions.'
      },
      {
        id: 2,
        question: 'How should you begin an enquiry letter?',
        type: 'multiple-choice',
        options: [
          'With a long introduction about yourself',
          'By clearly stating what information you need',
          'With complaints about competitors',
          'By demanding immediate response'
        ],
        correct: 'By clearly stating what information you need',
        explanation: 'Clear statements help the recipient understand exactly what information to provide.'
      },
      {
        id: 3,
        question: 'Complete this enquiry opening: "I am writing to _____ about..."',
        type: 'fill-in-blank',
        question_text: 'Fill in: "I am writing to _____ about your table tennis equipment."',
        correct: 'enquire',
        explanation: 'This clearly states the purpose of your letter as seeking information.'
      },
      {
        id: 4,
        question: 'True or False: You should ask specific questions to get useful information.',
        type: 'true-false',
        correct: true,
        explanation: 'Specific questions help you get the exact information you need to make decisions.'
      },
      {
        id: 5,
        question: 'What type of information should you request in a product enquiry?',
        type: 'multiple-choice',
        options: [
          'Only the price',
          'Prices, specifications, availability, and delivery options',
          'Just the company history',
          'Only customer reviews'
        ],
        correct: 'Prices, specifications, availability, and delivery options',
        explanation: 'Comprehensive information helps you make informed purchasing decisions.'
      },
      {
        id: 6,
        question: 'How should you organize your questions?',
        type: 'multiple-choice',
        options: [
          'Randomly throughout the letter',
          'In a logical, easy-to-follow sequence',
          'All in one long paragraph',
          'Only at the very end'
        ],
        correct: 'In a logical, easy-to-follow sequence',
        explanation: 'Logical organization makes it easier for the recipient to provide complete answers.'
      },
      {
        id: 7,
        question: 'What should you mention about your background?',
        type: 'multiple-choice',
        options: [
          'Your entire life story',
          'Relevant context that explains why you need the information',
          'Personal problems unrelated to the enquiry',
          'Nothing about yourself'
        ],
        correct: 'Relevant context that explains why you need the information',
        explanation: 'Relevant context helps the recipient provide more appropriate information.'
      },
      {
        id: 8,
        question: 'Fill in: "I would be grateful if you could _____ me with this information."',
        type: 'fill-in-blank',
        question_text: 'Complete: "I would be grateful if you could _____ me with this information."',
        correct: 'provide',
        explanation: 'This politely requests the information you need.'
      },
      {
        id: 9,
        question: 'True or False: You should mention how you found out about the company.',
        type: 'true-false',
        correct: true,
        explanation: 'This information helps companies understand their marketing effectiveness.'
      },
      {
        id: 10,
        question: 'How should you conclude an enquiry letter?',
        type: 'multiple-choice',
        options: [
          'With demands for immediate response',
          'With thanks in advance and contact information',
          'With threats if they don\'t respond',
          'With no closing statement'
        ],
        correct: 'With thanks in advance and contact information',
        explanation: 'Polite closure with contact details facilitates their response to your enquiry.'
      }
    ]
  },

  // Letters/emails of promotion - Christmas Programme
  {
    id: 'quiz-promotion-christmas-programme',
    essayId: 'promotion-christmas-programme',
    title: 'Promotional Letter - Writing Skills Quiz',
    category: 'Letters/emails of promotion',
    description: 'Master promotional writing through the Christmas programme example',
    difficulty: 'intermediate',
    totalQuestions: 10,
    timeLimit: 300,
    image: '🎄',
    questions: [
      {
        id: 1,
        question: 'What is the main purpose of a promotional letter?',
        type: 'multiple-choice',
        options: [
          'To provide neutral information',
          'To persuade people to buy, attend, or participate in something',
          'To complain about competitors',
          'To request information'
        ],
        correct: 'To persuade people to buy, attend, or participate in something',
        explanation: 'Promotional letters aim to generate interest and encourage action.'
      },
      {
        id: 2,
        question: 'How should you grab attention in a promotional letter?',
        type: 'multiple-choice',
        options: [
          'With boring statistics',
          'With an exciting opening that highlights benefits',
          'With complaints about alternatives',
          'With a long company history'
        ],
        correct: 'With an exciting opening that highlights benefits',
        explanation: 'Engaging openings capture interest and encourage continued reading.'
      },
      {
        id: 3,
        question: 'Complete this promotional opening: "Don\'t miss this _____ opportunity to..."',
        type: 'fill-in-blank',
        question_text: 'Fill in: "Don\'t miss this _____ opportunity to enjoy our Christmas programme."',
        correct: 'exciting',
        explanation: 'Positive adjectives create enthusiasm and urgency.'
      },
      {
        id: 4,
        question: 'True or False: You should focus on benefits to the reader rather than just features.',
        type: 'true-false',
        correct: true,
        explanation: 'Benefits show how the product/service will improve the reader\'s life.'
      },
      {
        id: 5,
        question: 'What should you emphasize in promotional writing?',
        type: 'multiple-choice',
        options: [
          'Technical specifications only',
          'What makes your offer special and valuable',
          'Why competitors are bad',
          'Company profits'
        ],
        correct: 'What makes your offer special and valuable',
        explanation: 'Unique value propositions differentiate your offer from alternatives.'
      },
      {
        id: 6,
        question: 'How should you create urgency in promotional letters?',
        type: 'multiple-choice',
        options: [
          'With false threats',
          'By mentioning limited time offers or limited availability',
          'By being aggressive',
          'By offering unrealistic promises'
        ],
        correct: 'By mentioning limited time offers or limited availability',
        explanation: 'Genuine scarcity motivates prompt action without being manipulative.'
      },
      {
        id: 7,
        question: 'What tone should promotional letters maintain?',
        type: 'multiple-choice',
        options: [
          'Boring and factual only',
          'Enthusiastic and positive while remaining credible',
          'Aggressive and pushy',
          'Apologetic and uncertain'
        ],
        correct: 'Enthusiastic and positive while remaining credible',
        explanation: 'Enthusiasm is contagious, but credibility maintains trust.'
      },
      {
        id: 8,
        question: 'Fill in: "This programme offers _____ value for money."',
        type: 'fill-in-blank',
        question_text: 'Complete: "This programme offers _____ value for money."',
        correct: 'excellent',
        explanation: 'Emphasizing value helps justify the cost to potential customers.'
      },
      {
        id: 9,
        question: 'True or False: You should include a clear call to action.',
        type: 'true-false',
        correct: true,
        explanation: 'Clear calls to action tell readers exactly what you want them to do next.'
      },
      {
        id: 10,
        question: 'How should you conclude a promotional letter?',
        type: 'multiple-choice',
        options: [
          'With a weak suggestion',
          'With a strong call to action and contact information',
          'With apologies for bothering them',
          'With no specific request'
        ],
        correct: 'With a strong call to action and contact information',
        explanation: 'Strong conclusions with clear next steps maximize response rates.'
      }
    ]
  },

  // Notices - Chess Competition
  {
    id: 'quiz-notice-chess-competition',
    essayId: 'notice-chess-competition',
    title: 'Notice Writing - Writing Skills Quiz',
    category: 'Notices',
    description: 'Learn notice writing through the chess competition example',
    difficulty: 'beginner',
    totalQuestions: 8,
    timeLimit: 240,
    image: '♟️',
    questions: [
      {
        id: 1,
        question: 'What is the main purpose of a notice?',
        type: 'multiple-choice',
        options: [
          'To have personal conversations',
          'To inform a group of people about important information',
          'To express personal opinions',
          'To tell stories'
        ],
        correct: 'To inform a group of people about important information',
        explanation: 'Notices are public announcements that convey important information to groups.'
      },
      {
        id: 2,
        question: 'What should appear at the top of a notice?',
        type: 'multiple-choice',
        options: [
          'Personal greetings',
          'The word "NOTICE" and the date',
          'Long explanations',
          'Contact details only'
        ],
        correct: 'The word "NOTICE" and the date',
        explanation: 'Clear heading and date help readers immediately understand the document type and currency.'
      },
      {
        id: 3,
        question: 'True or False: Notices should be brief and to the point.',
        type: 'true-false',
        correct: true,
        explanation: 'Concise notices are more likely to be read and understood quickly.'
      },
      {
        id: 4,
        question: 'What essential information should a notice include?',
        type: 'multiple-choice',
        options: [
          'Only the event name',
          'What, when, where, who, and how to participate',
          'Personal opinions about the event',
          'Detailed background history'
        ],
        correct: 'What, when, where, who, and how to participate',
        explanation: 'The 5 W\'s and H provide all necessary information for readers to take action.'
      },
      {
        id: 5,
        question: 'How should information be organized in a notice?',
        type: 'multiple-choice',
        options: [
          'Randomly scattered',
          'In logical order with clear headings or bullet points',
          'In one long paragraph',
          'Only in complex sentences'
        ],
        correct: 'In logical order with clear headings or bullet points',
        explanation: 'Clear organization makes information easy to find and understand quickly.'
      },
      {
        id: 6,
        question: 'Fill in: "All interested students should _____ by Friday."',
        type: 'fill-in-blank',
        question_text: 'Complete: "All interested students should _____ by Friday."',
        correct: 'register',
        explanation: 'This clearly states the required action and deadline.'
      },
      {
        id: 7,
        question: 'True or False: Notices should include contact information for questions.',
        type: 'true-false',
        correct: true,
        explanation: 'Contact information allows people to get clarification if needed.'
      },
      {
        id: 8,
        question: 'What tone should a notice maintain?',
        type: 'multiple-choice',
        options: [
          'Casual and chatty',
          'Formal but accessible',
          'Overly technical',
          'Emotional and personal'
        ],
        correct: 'Formal but accessible',
        explanation: 'Formal tone shows professionalism while remaining understandable to all readers.'
      }
    ]
  },

  // Proposals - DVD Collection
  {
    id: 'quiz-proposal-dvd-collection',
    essayId: 'proposal-dvd-collection',
    title: 'Proposal Writing - Writing Skills Quiz',
    category: 'Proposals',
    description: 'Master proposal writing through the DVD collection example',
    difficulty: 'advanced',
    totalQuestions: 12,
    timeLimit: 360,
    image: '💿',
    questions: [
      {
        id: 1,
        question: 'What is the main purpose of a proposal?',
        type: 'multiple-choice',
        options: [
          'To complain about current situations',
          'To suggest solutions to problems or improvements to existing systems',
          'To provide general information',
          'To criticize decision-makers'
        ],
        correct: 'To suggest solutions to problems or improvements to existing systems',
        explanation: 'Proposals present ideas for positive change or improvement.'
      },
      {
        id: 2,
        question: 'How should you begin a proposal?',
        type: 'multiple-choice',
        options: [
          'With personal opinions',
          'By clearly identifying the problem or need',
          'With criticism of current management',
          'With detailed budget information'
        ],
        correct: 'By clearly identifying the problem or need',
        explanation: 'Establishing the need creates context for your proposed solution.'
      },
      {
        id: 3,
        question: 'Complete this proposal opening: "I am writing to _____ that we establish..."',
        type: 'fill-in-blank',
        question_text: 'Fill in: "I am writing to _____ that we establish a DVD collection."',
        correct: 'propose',
        explanation: 'This clearly states the purpose of your document as presenting a proposal.'
      },
      {
        id: 4,
        question: 'True or False: You should provide evidence to support your proposal.',
        type: 'true-false',
        correct: true,
        explanation: 'Evidence makes your proposal more convincing and credible.'
      },
      {
        id: 5,
        question: 'What should you include when presenting your solution?',
        type: 'multiple-choice',
        options: [
          'Vague suggestions only',
          'Detailed explanation of how it will work and its benefits',
          'Criticism of alternatives',
          'Personal preferences only'
        ],
        correct: 'Detailed explanation of how it will work and its benefits',
        explanation: 'Detailed explanations help decision-makers understand and evaluate your proposal.'
      },
      {
        id: 6,
        question: 'How should you address potential concerns?',
        type: 'multiple-choice',
        options: [
          'Ignore them completely',
          'Acknowledge them and provide solutions or explanations',
          'Dismiss them as unimportant',
          'Blame others for having concerns'
        ],
        correct: 'Acknowledge them and provide solutions or explanations',
        explanation: 'Addressing concerns shows thorough thinking and increases proposal acceptance.'
      },
      {
        id: 7,
        question: 'What information should you include about implementation?',
        type: 'multiple-choice',
        options: [
          'No practical details',
          'Timeline, resources needed, and responsible parties',
          'Only the final outcome',
          'Criticism of current methods'
        ],
        correct: 'Timeline, resources needed, and responsible parties',
        explanation: 'Implementation details show feasibility and help decision-makers plan.'
      },
      {
        id: 8,
        question: 'Fill in: "This proposal would _____ the learning experience for students."',
        type: 'fill-in-blank',
        question_text: 'Complete: "This proposal would _____ the learning experience for students."',
        correct: 'enhance',
        explanation: 'This emphasizes the positive impact of your proposal.'
      },
      {
        id: 9,
        question: 'True or False: You should include cost considerations in your proposal.',
        type: 'true-false',
        correct: true,
        explanation: 'Cost information helps decision-makers evaluate feasibility and budget requirements.'
      },
      {
        id: 10,
        question: 'How should you present the benefits of your proposal?',
        type: 'multiple-choice',
        options: [
          'As minor improvements',
          'As significant, measurable advantages',
          'As personal preferences',
          'As temporary solutions'
        ],
        correct: 'As significant, measurable advantages',
        explanation: 'Strong benefits justify the investment of time and resources.'
      },
      {
        id: 11,
        question: 'What tone should a proposal maintain?',
        type: 'multiple-choice',
        options: [
          'Overly casual',
          'Professional, persuasive, and confident',
          'Apologetic and uncertain',
          'Aggressive and demanding'
        ],
        correct: 'Professional, persuasive, and confident',
        explanation: 'This tone inspires confidence in your idea while maintaining professionalism.'
      },
      {
        id: 12,
        question: 'How should you conclude a proposal?',
        type: 'multiple-choice',
        options: [
          'With apologies for suggesting changes',
          'With a strong summary and call for action',
          'With criticism of current systems',
          'With no specific conclusion'
        ],
        correct: 'With a strong summary and call for action',
        explanation: 'Strong conclusions reinforce your message and encourage decision-makers to act.'
      }
    ]
  }
];

// Quiz categories for filtering and organization
export const quizCategories = [
  { id: 'all', name: 'All Quizzes', icon: '📚' },
  { id: 'Letters/emails to inform', name: 'Informative Letters', icon: '📋' },
  { id: 'Letters/emails of invitation', name: 'Invitation Letters', icon: '🎉' },
  { id: 'Letters/emails of application', name: 'Application Letters', icon: '📄' },
  { id: 'Responding to letters/emails of complaint', name: 'Complaint Responses', icon: '🔧' },
  { id: 'Informal letters/emails', name: 'Informal Emails', icon: '💌' },
  { id: 'Letters/emails of request', name: 'Request Letters', icon: '🙏' },
  { id: 'Letters/emails of advice', name: 'Advice Letters', icon: '💡' },
  { id: 'Letters/emails of complaint', name: 'Complaint Letters', icon: '⚠️' },
  { id: 'Letters/emails to the editor', name: 'Editor Letters', icon: '📰' },
  { id: 'Letters/emails of enquiry', name: 'Enquiry Letters', icon: '❓' },
  { id: 'Letters/emails of promotion', name: 'Promotional Letters', icon: '🎯' },
  { id: 'Notices', name: 'Notices', icon: '📢' },
  { id: 'Proposals', name: 'Proposals', icon: '📋' }
];

// Quiz difficulty levels
export const difficultyLevels = {
  beginner: { color: 'bg-green-100 text-green-800', icon: '🌱' },
  intermediate: { color: 'bg-yellow-100 text-yellow-800', icon: '🌿' },
  advanced: { color: 'bg-red-100 text-red-800', icon: '🌳' }
};

// Quiz statistics and progress tracking
export const quizStats = {
  totalQuizzes: quizSets.length,
  totalQuestions: quizSets.reduce((sum, quiz) => sum + quiz.totalQuestions, 0),
  averageTimePerQuiz: Math.round(quizSets.reduce((sum, quiz) => sum + quiz.timeLimit, 0) / quizSets.length / 60),
  categoriesCount: quizCategories.length - 1 // excluding 'all'
};

// Helper function to get quiz by essay ID
export const getQuizByEssayId = (essayId) => {
  return quizSets.find(quiz => quiz.essayId === essayId);
};

// Helper function to get quizzes by category
export const getQuizzesByCategory = (category) => {
  if (category === 'all') return quizSets;
  return quizSets.filter(quiz => quiz.category === category);
};

// Helper function to get quiz difficulty info
export const getQuizDifficulty = (difficulty) => {
  return difficultyLevels[difficulty] || difficultyLevels.intermediate;
}; 